import { useEffect, useRef, useState } from 'react'
import {
  landingRouletteClaim,
  type LandingRouletteHistoryItem,
} from '@/lib/landing-roulette'
import styles from './LandingRouletteClaims.module.css'

export function LandingRouletteClaims({
  prizes,
  token,
  campaignId,
  onClaimed,
  autoOpen = true,
}: {
  prizes: LandingRouletteHistoryItem[]
  token: string
  campaignId: number
  onClaimed: (prize: LandingRouletteHistoryItem) => void
  autoOpen?: boolean
}) {
  const dialog = useRef<HTMLDialogElement>(null)
  const [busy, setBusy] = useState<number | null>(null)
  const [error, setError] = useState('')
  const [claimed, setClaimed] = useState<LandingRouletteHistoryItem[]>([])
  const pending = prizes.filter(
    (prize) => prize.prize_type === 'lucky_number' && prize.status === 'pending'
  )
  const pendingKey = pending.map((prize) => prize.id).join(',')

  useEffect(() => {
    if (autoOpen && pendingKey && !dialog.current?.open) dialog.current?.showModal()
  }, [pendingKey, autoOpen])

  async function claim(prize: LandingRouletteHistoryItem) {
    if (busy !== null) return
    setBusy(prize.id)
    setError('')
    try {
      const result = await landingRouletteClaim(token, campaignId, prize.id)
      setClaimed((items) => [...items.filter((item) => item.id !== result.id), result])
      onClaimed(result)
    } catch {
      setError(
        'Não foi possível concluir o resgate. Seu prêmio continua pendente. Tente novamente.'
      )
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      {pending.length > 0 && (
        <button
          className={styles.open}
          type="button"
          onClick={() => dialog.current?.showModal()}
        >
          {pending.some((prize) =>
            ['scratch_card', 'achievement', 'purchase_scratch_card'].includes(prize.prize_payload?.source ?? '')
          )
            ? 'Você ganhou números extras! Resgatar'
            : 'Você ganhou números na roleta! Resgatar'}
        </button>
      )}
      <dialog
        ref={dialog}
        className={styles.dialog}
        aria-labelledby="roulette-claim-title"
      >
        <h2 id="roulette-claim-title">
          {pending.some((prize) =>
            ['scratch_card', 'achievement', 'purchase_scratch_card'].includes(prize.prize_payload?.source ?? '')
          )
            ? 'Seus números extras'
            : 'Seus números da roleta'}
        </h2>
        <p>Parabéns! Resgate seus números para ativar sua participação.</p>
        {error && <p role="alert">{error}</p>}
        {[
          ...pending.filter((prize) => !claimed.some((item) => item.id === prize.id)),
          ...claimed,
        ].map((prize) => (
          <article key={prize.id} className={styles.prize}>
            <strong>
              {prize.prize_payload?.quantity ?? 1} números ganhos{' '}
              {['scratch_card', 'purchase_scratch_card'].includes(prize.prize_payload?.source ?? '')
                ? 'na raspadinha'
                : prize.prize_payload?.source === 'achievement'
                  ? `pelo selo ${prize.prize_payload.label ?? ''}`
                  : 'na roleta'}
            </strong>
            <p className={styles.numbers}>
              {prize.prize_payload?.lucky_numbers?.join(' · ')}
            </p>
            {prize.status === 'used' ? (
              <p role="status">
                ✓ Resgatado — seus números estão ativos em Meus números.
              </p>
            ) : (
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => void claim(prize)}
              >
                {busy === prize.id ? 'Resgatando…' : 'Resgatar'}
              </button>
            )}
          </article>
        ))}
        <button
          className={styles.close}
          type="button"
          onClick={() => dialog.current?.close()}
        >
          Fechar
        </button>
      </dialog>
    </>
  )
}
